import { Component } from '@theme/component';

/**
 * @typedef {Object} TextCarouselRefs
 * @property {HTMLElement} slides - Slides container
 * @property {HTMLElement[]} slideButtons - Slide elements
 * @property {HTMLElement} dotsContainer - Dots container
 * @property {HTMLButtonElement[]} dots - Dot button elements
 */

/**
 * Text carousel with fade transitions between text slides.
 * Desktop: click text columns to switch active. Mobile: crossfade slider with dots.
 *
 * @extends {Component<TextCarouselRefs>}
 */
class TextCarousel extends Component {
  #current = 0;
  #interval = undefined;
  #touchStartX = 0;
  #touchEndX = 0;
  #transitionTimer = null;

  requiredRefs = ['slides', 'slideButtons'];

  connectedCallback() {
    super.connectedCallback();

    this.#current = 0;
    this.#updateSlides(0);

    this.refs.slideButtons?.forEach((btn, i) => {
      btn.addEventListener('click', () => this.selectSlide(String(i)));
      btn.addEventListener('keydown', (e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          this.selectSlide(String(i));
        }
      });
    });

    if (this.autoplay) {
      this.play();
      document.addEventListener('visibilitychange', this.#handleVisibilityChange);
    }

    this.refs.slides.addEventListener('touchstart', this.#handleTouchStart, { passive: true });
    this.refs.slides.addEventListener('touchend', this.#handleTouchEnd, { passive: true });

    this.refs.slides.addEventListener('click', this.#handleSlideClick);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#suspend();
    clearTimeout(this.#transitionTimer);
    document.removeEventListener('visibilitychange', this.#handleVisibilityChange);
    this.refs.slides?.removeEventListener('touchstart', this.#handleTouchStart);
    this.refs.slides?.removeEventListener('touchend', this.#handleTouchEnd);
    this.refs.slides?.removeEventListener('click', this.#handleSlideClick);
  }

  get autoplay() {
    return Boolean(this.autoplayInterval);
  }

  get autoplayInterval() {
    const interval = this.getAttribute('autoplay');
    const value = parseInt(interval, 10);
    return Number.isNaN(value) ? undefined : value * 1000;
  }

  play(interval = this.autoplayInterval) {
    if (!this.autoplay) return;
    this.#suspend();
    this.#interval = setInterval(() => {
      if (document.hidden) return;
      const next = (this.#current + 1) % (this.refs.slideButtons?.length ?? 3);
      this.#updateSlides(next);
    }, interval);
    this.#setAriaLive('off');
  }

  /**
   * Handles slide selection from click.
   * @param {string} index - Slide index
   */
  selectSlide(index) {
    const idx = parseInt(index, 10);
    if (idx === this.#current) return;
    this.#updateSlides(idx);

    if (this.autoplay) {
      this.#suspend();
      this.play();
    }
  }

  /** Advances to the next slide, wrapping around. */
  nextSlide() {
    const total = this.refs.slideButtons?.length ?? 1;
    const next = (this.#current + 1) % total;
    this.#updateSlides(next);

    if (this.autoplay) {
      this.#suspend();
      this.play();
    }
  }

  /**
   * Handles tap on the active slide on mobile to advance.
   * @param {MouseEvent} e
   */
  #handleSlideClick = (e) => {
    if (window.innerWidth >= 750) return;

    const slide = e.target.closest('.tc__slide--active');
    if (slide) this.nextSlide();
  };

  #updateSlides(index) {
    const slides = this.refs.slideButtons ?? [];
    const dots = this.refs.dots ?? [];

    dots.forEach((dot, i) => {
      dot.classList.toggle('tc__dot--active', i === index);
    });

    // Mobile: sequential fade out then fade in
    if (window.innerWidth < 750 && slides[this.#current]?.classList.contains('tc__slide--active')) {
      clearTimeout(this.#transitionTimer);
      slides.forEach((s) => s.classList.remove('tc__slide--active'));
      this.#current = index;

      this.#transitionTimer = setTimeout(() => {
        slides[index]?.classList.add('tc__slide--active');
        this.#transitionTimer = null;
      }, 400);
      return;
    }

    // Desktop: simultaneous toggle
    this.#current = index;
    slides.forEach((slide, i) => {
      slide.classList.toggle('tc__slide--active', i === index);
    });
  }

  /**
   * Sets aria-live on the slides container.
   * @param {'off' | 'polite'} value
   */
  #setAriaLive(value) {
    this.refs.slides?.setAttribute('aria-live', value);
  }

  #suspend = () => {
    clearInterval(this.#interval);
    this.#interval = undefined;
    this.#setAriaLive('polite');
  };

  #handleVisibilityChange = () => {
    if (document.hidden) {
      this.#suspend();
    } else if (this.autoplay) {
      this.play();
    }
  };

  #handleTouchStart = (e) => {
    this.#touchStartX = e.changedTouches[0].screenX;
  };

  #handleTouchEnd = (e) => {
    this.#touchEndX = e.changedTouches[0].screenX;
    this.#handleSwipe();
  };

  #handleSwipe() {
    const diff = this.#touchStartX - this.#touchEndX;
    const threshold = 50;
    const total = this.refs.slideButtons?.length ?? 3;

    if (Math.abs(diff) < threshold) return;

    const next = diff > 0
      ? (this.#current + 1) % total
      : (this.#current - 1 + total) % total;

    this.#updateSlides(next);
    if (this.autoplay) {
      this.#suspend();
      this.play();
    }
  }
}

if (!customElements.get('text-carousel')) {
  customElements.define('text-carousel', TextCarousel);
}
